import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Chrono } from "react-chrono";
import "antd/dist/reset.css";
import { Button, Spin, Card, Statistic, DatePicker, Tooltip, message, Radio, Row, Col, Table, Dropdown, Space, Input, Modal, Progress, Tabs } from "antd";
import { DownOutlined, DownloadOutlined, SearchOutlined, UnorderedListOutlined, LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from 'xlsx';
import './audit.css';
import './TimelineView.css';
import MilestoneEventsPage from "./MilestoneEventsPage";
import MilestoneHistoryPage from "./MilestoneHistoryPage";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const { RangePicker } = DatePicker;

const buildEventKey = (item) => {
    const prevValue = String(item?.encompass?.previousValue ?? '');
    const newValue = String(item?.encompass?.newValue ?? '');
    return [
        item?.eventTime ?? '',
        item?.modifiedField ?? '',
        item?.description ?? '',
        item?.userId ?? '',
        prevValue,
        newValue
    ].join('||');
};

const GridView = React.memo(function GridView({
    gridContainerRef,
    gridColumns,
    allData,
    gridScrollX,
    lastEventTime,
    isLoadingMore,
    onLoadMore
}) {
    return (
        <div ref={gridContainerRef} style={{ overflowY: 'auto', maxHeight: 'none' }}>
            <Table
                virtual
                style={{ fontFamily: '"Proxima Nova", Arial, sans-serif !important' }}
                columns={gridColumns}
                dataSource={allData}
                rowKey="id"
                pagination={false}
                bordered
                size="middle"
                className="custom-grid-table"
                scroll={{ x: gridScrollX, y: 560 }}
            />
            <div className="sticky bottom-0 bg-white z-10 pt-2 pb-2 px-4 border-t border-gray-200">
                {lastEventTime && (
                    <div className="flex justify-end">
                        <Button
                            onClick={onLoadMore}
                            loading={isLoadingMore}
                            className="flex items-center gap-2 bg-[#006aa9] text-white hover:bg-[#006aa9]"
                        >
                            {isLoadingMore ? 'Loading...' : 'Load More'}
                            {!isLoadingMore && <DownOutlined />}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
});

const TimelineModeView = React.memo(function TimelineModeView({
    timelineContainerRef,
    data,
    lastEventTime,
    isLoadingMore,
    onLoadMore
}) {
    return (
        <div
            className="chrono-container"
            ref={timelineContainerRef}
            style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}
        >
            <Chrono
                items={data}
                mode="VERTICAL"
                theme={{
                    primary: "#006aa9",
                    secondary: "#f5f5f5",
                    cardBgColor: "#ffffff",
                    cardForeColor: "#333",
                    titleColor: "#4a6baf",
                    titleColorActive: "#006aa9"
                }}
                cardHeight="auto"
                className="responsive-chrono"
                fontSizes={{
                    cardSubtitle: '0.85rem',
                    cardText: '0.9rem',
                    cardTitle: '1rem'
                }}
                cardWidth={window.innerWidth < 768 ? '100%' : '600px'}
                allowDynamicUpdate={true}
                itemWidth={window.innerWidth < 768 ? 150 : 250}
            />
            <div className="flex justify-end p-4">
                {lastEventTime && (
                    <Button
                        onClick={onLoadMore}
                        loading={isLoadingMore}
                        className="flex items-center gap-2 bg-[#006aa9] text-white hover:bg-[#006aa9]"
                    >
                        {isLoadingMore ? 'Loading...' : 'Load More'}
                        {!isLoadingMore && <DownOutlined />}
                    </Button>
                )}
            </div>
        </div>
    );
});

const TimelineView = () => {
    // State for basic view
    const [basicLoanNumber, setBasicLoanNumber] = useState('');
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'timeline' or 'grid'
    const [isContentLoading, setIsContentLoading] = useState(false);
    const [isViewLoading, setIsViewLoading] = useState(false);
    // Existing state (preserved)
    const [dateRange, setDateRange] = useState([dayjs().subtract(1, 'week'), dayjs()]);
    const [inputFieldIDValue, setInputFieldIDValue] = useState('');
    const [inputUserIDValue, setInputUserIDValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [resetFlag, setResetFlag] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const [resourceId, setResourceId] = useState('');
    const [loanResourceId, setLoanResourceId] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [isPaginationLoading, setIsPaginationLoading] = useState(false);
    const [resourceType, setResourceType] = useState('LoanNumber');
    const initialDateRange = [dayjs().subtract(1, 'week'), dayjs()];
    const [rawData, setRawData] = useState([]);
    const [excelData, setExcelData] = useState([]);
    //const { getToken } = useAuth();
    const [allData, setAllData] = useState([]);
    const [lastEventTime, setLastEventTime] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    const allDataRef = useRef([]);
    const activeSearchRef = useRef({
        resourceId: '',
        loanNumber: '',
        userId: '',
        fieldValue: '',
        startDate: null,
        endDate: null
    });


    useEffect(() => {
        document.body.style.overflow = isDownloading ? "hidden" : "auto";
    }, [isDownloading]);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();

            initializeAuthObject();
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);
    useEffect(() => {
        if (resetFlag) {
            setResetFlag(false);
        }
    }, [resetFlag]);

    // keep loader on until Chrono renders
    useEffect(() => {
        if (viewMode === 'timeline' && data.length > 0) {
            const timeout = setTimeout(() => {
                setIsContentLoading(false);
            }, 300);
            return () => clearTimeout(timeout);
        }
    }, [data, viewMode]);

    useEffect(() => {
        allDataRef.current = allData;
    }, [allData]);

    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async function (loanObject) {
                loanObject.all().then(async function (loan) {
                    setLoanResourceId(loan?.loan?.id);
                    //handleApiCall(false, loan?.loan?.id);
                });
            });
        } catch (error) {
            console.error("Auth block Error:", error);
        }
    };
    const handleBasicSearch = async () => {
        if (!basicLoanNumber.trim()) {
            setError('Loan Number is required');
            return;
        }
        const onlyDigits = /^\d+$/.test(basicLoanNumber);
        if (resourceType === "LoanNumber" && !onlyDigits) {
            setError('Please enter valid Loan Number only');
            return;
        }

        setResourceType('LoanNumber');
        setResourceId(basicLoanNumber);
        await new Promise(resolve => setTimeout(resolve, 500));
        setCurrentPage(1);
        setIsLoading(true);
        setError(null);
        setData([]);
        handleApiCall(false, loanResourceId);
    };

    const handleApiCallButtonClick = async () => {

        setIsSearching(true);
        setCurrentPage(1);

        setError(null);
        setData([]);
        handleApiCall(false, loanResourceId);
        setIsSearching(false);
    };

    const handleReset = async () => {
        setIsLoading(false);
        setBasicLoanNumber('');
        setResourceId('');
        setInputFieldIDValue('');
        setInputUserIDValue('');
        setDateRange([...initialDateRange]);
        setCurrentPage(1);
        setData([]);
        setTotalCount(0);
        setError(null);
        setResetFlag(true);
        setShowAdvancedSearch(false);
        setInfoMessage('');
        activeSearchRef.current = {
            resourceId: '',
            loanNumber: '',
            userId: '',
            fieldValue: '',
            startDate: null,
            endDate: null
        };
    };

    const createTimelineItem = (change, index) => {
        const itemId = `${change.eventTime}-${index}`;
        const isLongValue =
            String(change.encompass?.previousValue).length > 20 ||
            String(change.encompass?.newValue).length > 20;
        const isLongFieldName = change.modifiedField.length > 20;
        return {
            id: itemId,
            title: dayjs(change.eventTime).format('YYYY-MM-DD HH:mm:ss'),
            cardTitle: (
                <h3 id={itemId} className={isLongFieldName ? 'card-title-long text-xs font-medium mb-3' : ''}>
                    {change.modifiedField}- {change.description}
                </h3>
            ),
            cardDetailedText: (
                <>
                    <div className="space-y-4">
                        {change.userId && (
                            <div className="flex flex-col xs:flex-row xs:items-center gap-2">
                                <span className=" text-gray-500 whitespace-nowrap">User Name</span>
                                <span className="px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                    {change.userId}
                                </span>
                            </div>
                        )}

                        <div className={`flex ${isLongValue ? 'flex-col' : 'flex-col sm:flex-row'} gap-3 items-stretch`}>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-gray-500 mb-1">Previous Value</div>
                                <div className="p-2 bg-gray-50 rounded border border-gray-200 text-gray-800 font-mono truncate">
                                    {String(change.encompass?.previousValue)}
                                </div>
                            </div>

                            {!isLongValue && (
                                <div className="hidden sm:flex items-center justify-center px-2 mt-3 text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            )}

                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-gray-500 mb-1">New Value</div>
                                <div className="p-2 bg-green-100 rounded border border-green-400 text-green-600 font-mono truncate">
                                    {String(change.encompass?.newValue)}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ),
        };
    };

    // Refs for scroll position
    const gridContainerRef = useRef(null);
    const timelineContainerRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const lastItemRef = useRef(null);

    const handleApiCall = async (loadMore = false, loanNumber = resourceId) => {
        if (!loadMore) {
            setIsLoading(true);
            setIsContentLoading(true);
            scrollPositionRef.current = 0;
            lastItemRef.current = null;
        } else {
            // Capture scroll position and *last rendered item* before loading more
            if (viewMode === 'timeline' && timelineContainerRef.current) {
                scrollPositionRef.current = timelineContainerRef.current.scrollTop; // save where user is
                lastItemRef.current = data.length ? data[data.length - 1].id : null; // <-- from timeline items, not allData
            }
            setIsLoadingMore(true);
        }

        setError(null);

        const freshSearchPayload = {
            resourceId: resourceType === "ResourceID" ? loanNumber : "",
            loanNumber: resourceType === "LoanNumber" ? loanNumber : "",
            userId: inputUserIDValue?.trim() || '',
            fieldValue: inputFieldIDValue?.trim() || '',
            startDate: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : null,
            endDate: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : null,
        };

        const appliedSearchPayload = loadMore ? activeSearchRef.current : freshSearchPayload;
        if (!loadMore) {
            activeSearchRef.current = freshSearchPayload;
        }

        const requestBody = {
            ...appliedSearchPayload,
            pageNumber: 1,
            pageSize: 30,
            lastEventTime: loadMore ? lastEventTime : null
        };

        try {

            const response = await fetch(`${API_DOMAIN}/api/audit/events/enhancedfilter-timed?download=false`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(requestBody),
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || "Request failed");
            }
            if (!result.success) {
                throw new Error(result.message || "Failed to fetch data");
            }

            const fieldChanges = result.data?.items.fieldChangeEvents.map((item, index) => {
                const uniqueEventKey = buildEventKey(item);
                return ({
                ...item,
                id: `${uniqueEventKey}||${index}`,
                uniqueEventKey,
                eventTimeStamp: new Date(item.eventTime).getTime(),
                eventTimeDisplayPT: `${dayjs(item.eventTime).tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm:ss')} PT`
                });
            });

            if (loadMore) {
                const byKey = new Map();
                for (const item of allDataRef.current) {
                    byKey.set(item.uniqueEventKey, item);
                }
                for (const item of fieldChanges) {
                    if (!byKey.has(item.uniqueEventKey)) {
                        byKey.set(item.uniqueEventKey, item);
                    }
                }

                const mergedData = Array.from(byKey.values());
                const mergedTimelineItems = mergedData.map((change, i) => createTimelineItem(change, i));
                setAllData(mergedData);
                setData(mergedTimelineItems);
                setTotalCount(mergedData.length);
            } else {
                // Initial load
                const timelineItems = fieldChanges.map((change, i) => createTimelineItem(change, i));
                setData(timelineItems);
                setAllData(fieldChanges);
                setTotalCount(fieldChanges.length);
            }

            // Update lastEventTime for pagination
            if (fieldChanges.length > 0) {
                const lastEventStamp = fieldChanges[fieldChanges.length - 1].eventTimeStamp;
                // Move back 1ms to avoid inclusive-cursor overlap without skipping records.
                setLastEventTime(new Date(lastEventStamp - 1).toISOString());
            }
            setInfoMessage(null);

        } catch (error) {
            console.error("Error fetching filtered data:", error);
            setError(error.message || "An unexpected error occurred");
            if (!loadMore) {
                setData([]);
                setAllData([]);
            }
            setTotalCount(0);
        } finally {
            if (!loadMore) {
                setIsLoading(false);
                if (viewMode === 'grid') {
                    setIsContentLoading(false); // grid hides immediately
                }
            } else {
                setIsLoadingMore(false);
            }
        }
    };

    const exportToExcel = async () => {
        setIsDownloading(true);
        setDownloadProgress(10);


        const requestBody = {
            resourceId: resourceType === "ResourceID" ? loanResourceId : "",
            loanNumber: resourceType === "LoanNumber" ? loanResourceId : "",
            userId: inputUserIDValue,
            fieldValue: inputFieldIDValue,
            startDate: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : null,
            endDate: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : null,
            pageNumber: 0,
            pageSize: 0
        };

        try {
            //const token = await getToken();
            setDownloadProgress(25);

            const response = await fetch(
                `${API_DOMAIN}/api/audit/events/enhancedfilter-timed?download=true`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            setDownloadProgress(60);

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || "Failed to fetch data");
            }

            const fieldChanges = result.data?.items.fieldChangeEvents || [];

            if (fieldChanges.length === 0) {
                setInfoMessage("No events found matching your criteria for download");
                setDownloadProgress(100);
                return;
            }

            setDownloadProgress(75);

            const exportData = fieldChanges.map(item => ({
                'Timestamp': dayjs(item.eventTime).tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm:ss') + ' PT',
                'Field Name': item.modifiedField,
                'User Name': item.userId || '',
                'Previous Value': String(item.encompass?.previousValue || ''),
                'New Value': String(item.encompass?.newValue || '')
            }));

            setDownloadProgress(85);

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(exportData);
            XLSX.utils.book_append_sheet(wb, ws, "Timeline Data");

            setDownloadProgress(95);
            // ⬇️ allow UI to repaint
            await sleep(50);
            XLSX.writeFile(
                wb,
                `Audit_data_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`
            );

            setDownloadProgress(100);
            message.success("Downloaded successfully!!!");

        } catch (error) {
            console.error("Error fetching filtered data:", error);
            message.error(error.message || "Download failed");
            excelData([]);
        } finally {
            setTimeout(() => {
                setIsDownloading(false);
                setDownloadProgress(0);
            }, 500);
        }
    };


    // Grid view columns configuration
    const gridColumns = useMemo(() => [
        {
            title: 'Field Name',
            dataIndex: 'modifiedField',
            key: 'modifiedField',
            width: 200,
            fixed: 'left',
            shouldCellUpdate: (record, prevRecord) => record.modifiedField !== prevRecord.modifiedField || record.description !== prevRecord.description,
            render: (text, record) => {
                const description = record?.description || ''; // read description
                return (
                    <div>
                        {text} {description ? ` - ${description}` : ''}
                    </div>
                );
            },
        },
        {
            title: 'User Name',
            dataIndex: 'userId',
            key: 'userId',
            width: 150,
            shouldCellUpdate: (record, prevRecord) => record.userId !== prevRecord.userId,
            render: (text) => (
                <div className="text-blue-600">
                    {text}
                </div>
            ),
        },
        {
            title: 'Previous Value',
            dataIndex: ['encompass', 'previousValue'],
            key: 'previousValue',
            width: 150,
            shouldCellUpdate: (record, prevRecord) => record?.encompass?.previousValue !== prevRecord?.encompass?.previousValue,
            render: (text) => (
                <div className="bg-gray-100 p-1 rounded">
                    {String(text || 'N/A')}
                </div>
            ),
        },
        {
            title: 'New Value',
            dataIndex: ['encompass', 'newValue'],
            key: 'newValue',
            width: 150,
            shouldCellUpdate: (record, prevRecord) => record?.encompass?.newValue !== prevRecord?.encompass?.newValue,
            render: (text) => (
                <div className="bg-blue-100 p-1 rounded text-blue-800">
                    {String(text || 'N/A')}
                </div>
            ),
        },
        {
            title: 'Timestamp',
            dataIndex: 'eventTime',
            key: 'eventTime',
            width: 180,
            sorter: (a, b) => a.eventTimeStamp - b.eventTimeStamp,
            sortDirections: ['descend', 'ascend'],
            shouldCellUpdate: (record, prevRecord) => record.eventTimeDisplayPT !== prevRecord.eventTimeDisplayPT,
            render: (_, record) => (
                <div className="text-gray-600">
                    {record.eventTimeDisplayPT}
                </div>
            ),
            defaultSortOrder: 'descend',
        },
    ], []);

    const gridScrollX = useMemo(
        () => gridColumns.reduce((acc, col) => acc + (col.width || 150), 0),
        [gridColumns]
    );

    const handleLoadMore = useCallback(() => {
        const activeLoanNumber = activeSearchRef.current?.loanNumber || loanResourceId;
        handleApiCall(true, activeLoanNumber);
    }, [loanResourceId, lastEventTime, viewMode, data]);

    const handleClearFilters = useCallback(() => {
        setInputFieldIDValue('');
        setInputUserIDValue('');
    }, []);

    const toggleViewMode = () => {
        setIsViewLoading(true);
        setViewMode(prevMode => {
            const newMode = prevMode === 'timeline' ? 'grid' : 'timeline';
            setTimeout(() => setIsViewLoading(false), 500);
            return newMode;
        });
    };

    const [activeTab, setActiveTab] = useState('1');
    const [milestoneTabKey, setMilestoneTabKey] = useState(Date.now());

    const [milestoneHistoryTabKey, setMilestoneHistoryTabKey] = useState(Date.now());

    const handleTabChange = (key) => {
        setActiveTab(key);
        if (key === '2') {
            setMilestoneTabKey(Date.now());
        }
        if (key === '3') {
            setMilestoneHistoryTabKey(Date.now());
        }
    };

    // Dynamic tab label style based on selection
    const getTabLabel = (label, key) => (
        <span
            style={{
                padding: key === '2' ? '0 24px' : '0 20px',
                fontWeight: 400,
                fontSize: 14,
                color: activeTab === key ? '#fff' : '#006aa9',
                background: activeTab === key ? '#006aa9' : 'none',
                borderRadius: 6,
                letterSpacing: 0.2,
                fontFamily: 'inherit',
                display: 'inline-block',
                border: 'none',
                transition: 'color 0.2s, background 0.2s',
                minWidth: 120,
                textAlign: 'center',
                lineHeight: '32px',
            }}
        >
            {label}
        </span>
    );

    const resultsContent = useMemo(() => {
        if (isContentLoading || isViewLoading) {
            return (
                <div className="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50">
                    <div className="text-center">
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                        <p className="mt-4 text-lg">Loading...</p>
                    </div>
                </div>
            );
        }

        if (data.length === 0) {
            return (
                <Card className="text-center py-10">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No data</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        No audit record found. Please search by Field ID or User ID.
                    </p>
                </Card>
            );
        }

        return (
            <Card className="mb-3 relative"
                title={
                    <div className="flex justify-between items-center">
                        <span></span>
                        <div className="flex items-center gap-2">
                            <Statistic
                                title="Total Records"
                                value={totalCount}
                                className="inline-block"
                            />
                            <Tooltip title="Download as Excel">
                                <Button
                                    type="text"
                                    icon={<DownloadOutlined />}
                                    onClick={exportToExcel}
                                />
                            </Tooltip>
                        </div>
                    </div>
                }
            >
                {viewMode === 'timeline' ? (
                    <TimelineModeView
                        timelineContainerRef={timelineContainerRef}
                        data={data}
                        lastEventTime={lastEventTime}
                        isLoadingMore={isLoadingMore}
                        onLoadMore={handleLoadMore}
                    />
                ) : (
                    <GridView
                        gridContainerRef={gridContainerRef}
                        gridColumns={gridColumns}
                        allData={allData}
                        gridScrollX={gridScrollX}
                        lastEventTime={lastEventTime}
                        isLoadingMore={isLoadingMore}
                        onLoadMore={handleLoadMore}
                    />
                )}
            </Card>
        );
    }, [
        isContentLoading,
        isViewLoading,
        data,
        totalCount,
        viewMode,
        lastEventTime,
        isLoadingMore,
        allData,
        gridColumns,
        gridScrollX,
        handleLoadMore,
        exportToExcel
    ]);

    const tabItems = [
        {
            key: '1',
            label: getTabLabel('Field Audit', '1'),
            children: (
                <div className="space-y-2">
                    {/* Existing content moved here */}
                    <Card className="mb-5">
                        <div className="space-y-4">
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                handleApiCallButtonClick();
                            }}>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className="grid grid-cols-2 gap-2">
                                            {/* User ID */}
                                            <div className="p-1 rounded-md custom-grid-table">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">User ID</label>
                                                <Input
                                                    size="middle"
                                                    value={inputUserIDValue}
                                                    onChange={(e) => setInputUserIDValue(e.target.value)}
                                                    placeholder="Enter User ID"
                                                    status={error?.includes('User ID') ? 'error' : ''}
                                                    onPressEnter={() => handleApiCallButtonClick()}
                                                />
                                                {error?.includes('User ID') && (
                                                    <div className="text-red-500 text-xs mt-1">{error}</div>
                                                )}
                                            </div>
                                            {/* Field ID */}
                                            <div className="p-1 rounded-md custom-grid-table">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Field ID</label>
                                                <Input
                                                    size="middle"
                                                    value={inputFieldIDValue}
                                                    onChange={(e) => setInputFieldIDValue(e.target.value)}
                                                    placeholder="Enter Field ID"
                                                    status={error?.includes('Field ID') ? 'error' : ''}
                                                    onPressEnter={() => handleApiCallButtonClick()}
                                                />
                                                {error?.includes('Field ID') && (
                                                    <div className="text-red-500 text-xs mt-1">{error}</div>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="flex gap-2 h-full items-end p-1">
                                            <Button
                                                className="bg-gray-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-[#E50956] flex items-center"
                                                onClick={handleClearFilters}
                                            >
                                                Clear
                                            </Button>
                                            <Button
                                                htmlType="submit"
                                                loading={isSearching}
                                                icon={<SearchOutlined />}
                                                className="bg-[#006aa9] text-white font-semibold py-2 px-4 rounded-md hover:bg-[#E50956] flex items-center"
                                            >
                                                Search
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </form>
                            {error && (
                                <div className="text-red-500 text-sm">
                                    {error}
                                </div>
                            )}
                        </div>
                    </Card>
                    {resultsContent}

                </div>
            )
        },
        {
            key: '2',
            label: getTabLabel('Milestone Events', '2'),
            children: <MilestoneEventsPage loanId={loanResourceId} key={milestoneTabKey} />
        },
        {
            key: '3',
            label: getTabLabel('Milestone History', '3'),
            children: <MilestoneHistoryPage loanId={loanResourceId} key={milestoneHistoryTabKey} />
        }
    ];

    return (
        <>
            {isDownloading && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 99999,
                        background: "rgba(255,255,255,0.75)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <div
                        style={{
                            width: 420,
                            background: "#fff",
                            padding: 24,
                            borderRadius: 8,
                            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                            textAlign: "center"
                        }}
                    >
                        <h3 style={{ marginBottom: 16 }}>Preparing Download</h3>

                        <Progress
                            percent={downloadProgress}
                            status="active"
                            strokeColor="#006aa9"
                        />

                        <p style={{ marginTop: 12 }}>
                            {downloadProgress < 60 && "Fetching data..."}
                            {downloadProgress >= 60 && downloadProgress < 90 && "Preparing Excel..."}
                            {downloadProgress >= 90 && "Finalizing download..."}
                        </p>
                    </div>
                </div>
            )}

            <div style={{ maxWidth: 1600, margin: '0 auto', padding: 18 }}>
                <div className="custom-tabs-container">
                    <Tabs
                        defaultActiveKey="1"
                        activeKey={activeTab}
                        onChange={handleTabChange}
                        type="card"
                        size="large"
                        tabBarGutter={0}
                        className="custom-tabs"
                        tabBarStyle={{ marginBottom: 20, fontWeight: 600, fontSize: 18 }}
                        items={tabItems}
                    />
                </div>
            </div>
        </>
    );

};

export default TimelineView;